import { headerMenuAtom } from "@/atoms/header-menu"
import { useAnimatedCurrency } from "@/hooks/use-animated-currency"
import { useCartBumpRotate, useCartBumpScale } from "@/hooks/use-cart-bump"
import { useFlushAndGo } from "@/hooks/use-flush-and-go"
import { useBagUnlocked } from "@/hooks/use-store-closed-guard"
import { useOptimisticCart } from "@/hooks/use-optimistic-cart"
import { CartIcon } from "@/icons"
import { CART_TARGET_ATTR } from "@/lib/fly-to-cart"
import { SPRING } from "@/lib/motion"
import { CountBadge, Icon, MotionButton, Spinner, Text } from "@/components/ui"
import { useAtom } from "jotai"
import { motion } from "motion/react"
import { useNavigate } from "react-router-dom"
import { BagCouponPill } from "./coupon/BagCouponPill"
import { BAG_COUPON_HASH } from "./coupon/BagCouponStrip"
import { FixedBottomBar } from "./FixedBottomBar"

export const FloatingCartButton = ({ className }: { className?: string }) => {
  const { cart } = useOptimisticCart()
  const bagUnlocked = useBagUnlocked()
  const { isBusy, run } = useFlushAndGo()
  const navigate = useNavigate()

  const [headerMenu] = useAtom(headerMenuAtom)

  const buttonControls = useCartBumpScale()
  const badgeControls = useCartBumpScale({
    add: 1.55,
    remove: 0.62,
    stiffness: SPRING.wobble.stiffness,
    damping: SPRING.wobble.damping,
    mass: SPRING.wobble.mass,
  })
  const iconControls = useCartBumpRotate()
  const total = useAnimatedCurrency(cart?.totalPrice ?? 0)

  if (!bagUnlocked) return null

  if (cart?.items.length === 0) return null

  if (headerMenu) return null

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    run(() => navigate("/cart"))
  }

  return (
    <FixedBottomBar className={className}>
      <BagCouponPill cart={cart} onOpen={() => run(() => navigate(`/cart${BAG_COUPON_HASH}`))} />
      <MotionButton
        onClick={handleClick}
        disabled={isBusy}
        animate={buttonControls}
        size="lg"
        align="between"
        fullWidth
      >
        <div className="relative flex items-center gap-2">
          <motion.div animate={badgeControls} className="absolute -top-1 z-raised ml-4">
            <CountBadge tone="on-primary" size="sm" count={cart.items.reduce((sum, item) => sum + item.quantity, 0)} />
          </motion.div>
          <motion.div animate={iconControls} {...{ [CART_TARGET_ATTR]: "" }}>
            <Icon as={CartIcon} size="lg" />
          </motion.div>
          <Text as="strong" size="lg" weight="extrabold" className="ml-4">
            <motion.span>{total}</motion.span>
          </Text>
        </div>
        {isBusy ? (
          <Text as="span" size="base" weight="bold" className="flex items-center gap-2">
            <Spinner />
            SALVANDO...
          </Text>
        ) : (
          <Text as="span" size="base" weight="bold">
            VER SACOLA
          </Text>
        )}
      </MotionButton>
    </FixedBottomBar>
  )
}
